/**
 * Org Context Navigation Service
 *
 * Prevents cross-organization data leakage via browser back/forward navigation.
 *
 * How it works:
 * 1. On every NavigationEnd, stamps the current orgId into history.state using
 *    Angular's Location.replaceState(). This annotates each history entry with
 *    which org was active when the page was visited.
 * 2. On NavigationStart triggered by popstate (back/forward button), reads the
 *    restored state's orgId and compares with the current orgId. If they differ,
 *    the navigation is cancelled and the user is redirected to /dashboard.
 *
 * SECURITY NOTE: This service provides UX-layer protection only. history.state
 * is attacker-controllable via DevTools (history.pushState). The backend enforces
 * authoritative org isolation via X-Organization-Id header validation and per-resource
 * org membership checks (e.g., course-crud.ts handler validates course.organization_id
 * against user.organizations). This service prevents accidental cross-org exposure
 * from normal back/forward navigation — it is not a security boundary.
 *
 * @requirements
 * - REQ-ORG-902: Frontend data isolation (prevent cross-org back-button leak)
 * - REQ-ORG-103: Organization switching
 *
 * @see KAN-122
 */

import { Injectable } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs';
import { AuthService } from '../../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class OrgContextNavigationService {
  private isRedirecting = false;

  constructor(
    private router: Router,
    private location: Location,
    private authService: AuthService
  ) {
    this.subscribeToNavigationEvents();
  }

  private subscribeToNavigationEvents(): void {
    // Root service — subscriptions live for the app lifetime, no teardown needed.

    // Stamp orgId into history.state on every successful navigation
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.stampOrgId(event));

    // Check for org mismatch on popstate (back/forward button) navigation
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event) => this.checkPopstateOrgMismatch(event));
  }

  private stampOrgId(_event: NavigationEnd): void {
    const currentOrgId = this.authService.getCurrentOrganizationId();
    if (!currentOrgId) {
      return;
    }

    // Use Location.replaceState to annotate the current history entry with orgId.
    // This preserves the URL but adds orgId to the state object so it can be
    // read when the user navigates back/forward.
    const currentPath = this.location.path(true);
    const currentState = this.location.getState() as Record<string, unknown> | null;
    this.location.replaceState(
      currentPath,
      '',
      { ...(currentState ?? {}), orgId: currentOrgId }
    );
  }

  private checkPopstateOrgMismatch(event: NavigationStart): void {
    // Only check popstate navigations (back/forward button)
    if (event.navigationTrigger !== 'popstate') {
      return;
    }

    // Don't re-check if we're already redirecting
    if (this.isRedirecting) {
      return;
    }

    const currentOrgId = this.authService.getCurrentOrganizationId();

    // If user is not authenticated or has no org, skip check
    if (!currentOrgId) {
      return;
    }

    // System admins can access all orgs -- don't block their cross-org navigation
    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.isSystemAdmin) {
      return;
    }

    const restoredState = event.restoredState as Record<string, unknown> | null;
    const restoredOrgId = restoredState?.['orgId'] as string | undefined;

    // If restored state has no orgId (pre-existing history entries before this
    // service was deployed), allow navigation -- the orgId will be stamped on
    // the subsequent NavigationEnd.
    if (!restoredOrgId) {
      return;
    }

    // If orgIds match, navigation is safe
    if (restoredOrgId === currentOrgId) {
      return;
    }

    // Org mismatch detected -- user is navigating back to a page from a
    // different organization. Cancel and redirect to dashboard.
    console.warn('[OrgContextNav] Blocked cross-org back navigation. Redirecting to dashboard.');

    this.isRedirecting = true;
    this.router.navigate(['/dashboard'], { replaceUrl: true })
      .then(() => { this.isRedirecting = false; })
      .catch(() => { this.isRedirecting = false; });
  }
}
